import React, { useState, useRef, useEffect } from 'react';
import { LanguageOption } from '../../types';

interface LanguageSwitcherProps {
  options: LanguageOption[];
  currentLanguage: string;
  className?: string;
}

const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({
  options,
  currentLanguage,
  className = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  const currentOption = options.find(option => option.code === currentLanguage);
  
  const supportedLocales = options.map(option => option.code);

  const handleLanguageChange = (locale: string) => {
    if (locale === currentLanguage) {
      return;
    }
    const { pathname, search, hash } = window.location;
    const pathParts = pathname.split('/').filter(Boolean);
    // If first segment is a supported locale, replace it
    if (supportedLocales.includes(pathParts[0])) {
      pathParts[0] = locale;
    } else {
      pathParts.unshift(locale);
    }
    const newPath = '/' + pathParts.join('/') + (search || '') + (hash || '');
    window.location.href = newPath;
  };
  
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  return (
    <div className={`relative inline-block text-left ${className}`} ref={dropdownRef}>
      <button
        type="button"
        className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-primary-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-secondary-500 focus:ring-offset-2"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="mr-1">{currentOption?.flag}</span>
        <span>{currentOption?.label}</span>
        <svg className="ml-2 -mr-0.5 h-4 w-4 rotate-180 sm:rotate-0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
      </button>
      
      {isOpen && (
        <div className="absolute left-0 right-0 z-50 w-40 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none bottom-full mb-2 sm:bottom-auto sm:top-full sm:mt-2 sm:right-0 sm:left-auto origin-bottom-right sm:origin-top-right">
          <div className="py-1" role="menu" aria-orientation="vertical" aria-labelledby="language-menu-button">
            {options.map((option) => (
              <button
                key={option.code}
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleLanguageChange(option.code);
                  setIsOpen(false);
                }}
                className={`
                  block px-4 py-2 text-sm w-full text-left cursor-pointer hover:bg-gray-50 transition-colors duration-200
                  ${option.code === currentLanguage ? 'bg-gray-100 text-primary-700' : 'text-gray-700'}
                `}
                role="menuitem"
              >
                <span className="mr-2">{option.flag}</span>
                {option.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;